import type { AutocompletePrediction, PlacesServiceStatus } from "@/modules/google/types"
import type { Location } from "@/modules/locations/locations"
import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"

import pDebounce from "p-debounce"
import { useMemo } from "react"

import { useGooglePlaces } from "@/modules/google/useGooglePlaces"
import { DEBOUNCE_DELAY } from "@/modules/jobAds/client/constants"
import { formatPredictionsToOptions, setLocationFromPlaceResult } from "@/modules/locations/locations"

type UseGoogleLocation = {
  fetchPredictionsDebounced: (
    input: string,
    options: {
      onSuccess: (predictions: SimpleSelectOptions[]) => void
      onZeroResult?: () => void
      onError?: () => void
      onSettled?: () => void
    }
  ) => Promise<void>

  fetchDetails: (
    placeId: string,
    options: {
      onSuccess: (location: Location) => void
      onError: () => void
    }
  ) => void
}

type AvailableLocationTypes = "(regions)"
type AvailableLocationFields = "address_component" | "geometry" | "place_id"

export const useGoogleLocation = (): UseGoogleLocation => {
  const context = useGooglePlaces()
  const errorText = context?.errorText
  const autocompleteService = context?.autocompleteService
  const placesService = context?.placesService
  const googleServiceSessionToken = context?.googleServiceSessionToken

  const fetchPredictionsDebounced = useMemo(() => {
    return pDebounce(
      (
        input: string,
        options: {
          onSuccess: (predictions: SimpleSelectOptions[]) => void
          onZeroResult?: () => void
          onError?: () => void
          onSettled?: () => void
        }
      ): void => {
        if (!autocompleteService || !googleServiceSessionToken || input === "") {
          return
        }

        if (errorText?.length) {
          options.onError?.()
        }

        const availableLocationTypes: AvailableLocationTypes[] = ["(regions)"]

        autocompleteService.getPlacePredictions(
          {
            input,
            sessionToken: googleServiceSessionToken,
            types: availableLocationTypes,
          },
          (predictions: AutocompletePrediction[] | null, status: PlacesServiceStatus): void => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            if (status === "OK") {
              options.onSuccess(formatPredictionsToOptions(predictions))
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            } else if (status === "ZERO_RESULTS") {
              options.onZeroResult?.()
            } else {
              options.onError?.()
            }
            options.onSettled?.()
          }
        )
      },
      DEBOUNCE_DELAY
    )
  }, [autocompleteService, googleServiceSessionToken, errorText])

  const fetchDetails = (
    placeId: string,
    options: {
      onSuccess: (location: Location) => void
      onError: () => void
    }
  ): void => {
    if (!placesService) {
      return
    }

    if (errorText?.length) {
      options.onError()
    }

    const availableLocationFields: AvailableLocationFields[] = ["address_component", "place_id", "geometry"]

    placesService.getDetails(
      {
        fields: availableLocationFields,
        placeId,
        sessionToken: googleServiceSessionToken || undefined,
      },
      setLocationFromPlaceResult(options.onSuccess, options.onError)
    )
  }

  return { fetchDetails, fetchPredictionsDebounced }
}
